import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEAI3DScene } from '@/components/deai-3dscene';
import { useAppStore } from '@/hooks/use-app-store';
import { MenuButton } from '@/components/MenuButton';
import { ClickEnhancer } from '@/components/ClickEnhancer';
import MainMenuSvg from '@/assets/Main menu_UI_v2.svg';
import hatSvg from '@/assets/hat.svg';
import bottleSvg from '@/assets/bottle.svg';
import omiboxSvg from '@/assets/omibox.svg';
import omistickSvg from '@/assets/omistick.svg';

export const HomePage = () => {
    const navigate = useNavigate();
    const { triggerAvatarAnimator } = useAppStore();
    const [button1Pressed, setButton1Pressed] = useState(false);
    const [button2Pressed, setButton2Pressed] = useState(false);

    useEffect(()=>{
        triggerAvatarAnimator(
            0,
            'StartIdle'
        );
    },[triggerAvatarAnimator])

    // Button click handlers with coordinate-based detection
    const handlePotionButtonClick = () => {
        console.log('Potion game selected!');
        setButton1Pressed(true);
        setTimeout(() => {
            setButton1Pressed(false);
            window.location.replace('/potioncraft');
        }, 150);
    };

    const handleOmikujiButtonClick = () => {
        console.log('Omikuji game selected!');
        setButton2Pressed(true);
        setTimeout(() => {
            setButton2Pressed(false);
            window.location.replace('/fortunetelling');
        }, 150);
    };

    return(<></>);

  return (
    <ClickEnhancer className="h-screen relative overflow-hidden">
        {/* Gradient Background */}
        <div 
          className="absolute inset-0 w-full h-full"
          style={{
            background: 'linear-gradient(to bottom, #231E36 0%, #1B172F 100%)',
            zIndex: -2
          }}
        />

        {/* SVG Menu Background - Centered (remove click handler since we have separate buttons now) */}
        <div className="absolute inset-0 flex items-center justify-center" style={{background: 'transparent'}}>
          <div 
            className="w-full h-full flex items-center justify-center relative"
            style={{
              maxWidth: '1080px',
              maxHeight: '1920px',
              aspectRatio: '1080 / 1920'
            }}
          >
            <img 
              src={MainMenuSvg}
              alt="Main Menu"
              className="w-full h-full object-contain pointer-events-none"
              style={{
                maxWidth: '1080px',
                maxHeight: '1920px',
                aspectRatio: '1080 / 1920'
              }}
            />
          </div>
        </div>

        {/* Potion Button - Positioned over the potion button area */}
        <div
          className="absolute z-20"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%) translateY(-190px) translateX(-250px)',
          }}
        >
          <MenuButton 
            onClick={handlePotionButtonClick}
            pressed={button1Pressed}
            buttonId="potion"
          />
        </div>

        {/* Omikuji Button - Positioned over the omikuji button area */}
        <div
          className="absolute z-20"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%) translateY(362px) translateX(-250px)',
          }}
        >
          <MenuButton 
            onClick={handleOmikujiButtonClick}
            pressed={button2Pressed}
            buttonId="omikuji"
          />
        </div>

        {/* SVG Pattern overlay for top-left triangular background */}
        <div 
          className="absolute inset-0 w-full h-full overflow-hidden"
          style={{ 
            clipPath: 'polygon(100% 0, 0% 100%, 100% 100%)',
            pointerEvents: 'none',
            zIndex: -1
          }}
        >
          <div 
            className="w-[600%] h-[600%] absolute"
            style={{
              opacity: 0.07,
              animation: 'movePatternTopLeft 30s linear infinite',
              top: '-100%',
              left: '-100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Doubled the number of repeats from 10 to 20 */}
            {Array.from({ length: 20 }).map((_, repeatIndex) => (
              <div key={`repeat-top-${repeatIndex}`}>
                {/* First row */}
                <div className="flex" style={{ marginTop: '40px' }}>
                  {/* Doubled the number of SVGs per row from 8 to 16 */}
                  {Array.from({ length: 16 }).map((_, colIndex) => (
                    <div 
                      key={`icon1-top-${repeatIndex}-${colIndex}`} 
                      className="mx-20 my-20"
                    >
                      <img 
                        src={colIndex % 2 === 0 ? omiboxSvg : omistickSvg} 
                        alt={colIndex % 2 === 0 ? "Omibox" : "Omistick"}
                        style={{
                          width: colIndex % 2 === 0 ? '150px' : '80px',
                          height: colIndex % 2 === 0 ? '140px' : '130px',
                          filter: 'brightness(0) invert(1)'
                        }}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Second row - offset by 35px */}
                <div className="flex" style={{ marginTop: '40px', marginLeft: '35px' }}>
                  {/* Doubled the number of SVGs per row from 8 to 16 */}
                  {Array.from({ length: 16 }).map((_, colIndex) => (
                    <div 
                      key={`icon2-top-${repeatIndex}-${colIndex}`} 
                      className="mx-20 my-20"
                    >
                      <img 
                        src={colIndex % 2 === 0 ? omistickSvg : omiboxSvg} 
                        alt={colIndex % 2 === 0 ? "Omistick" : "Omibox"}
                        style={{
                          width: colIndex % 2 === 0 ? '80px' : '150px',
                          height: colIndex % 2 === 0 ? '130px' : '140px',
                          filter: 'brightness(0) invert(1)'
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* SVG Pattern overlay for bottom-right triangular background */}
        <div 
          className="absolute inset-0 w-full h-full overflow-hidden"
          style={{ 
            clipPath: 'polygon(100% 0, 0% 100%, 0 0)',
            pointerEvents: 'none',
            zIndex: -1
          }}
        >
          <div 
            className="w-[600%] h-[600%] absolute"
            style={{
              opacity: 0.05,
              animation: 'movePattern 30s linear infinite',
              top: '-100%',
              left: '-100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Doubled the number of repeats from 10 to 20 */}
            {Array.from({ length: 20 }).map((_, repeatIndex) => (
              <div key={`repeat-${repeatIndex}`}>
                {/* First row */}
                <div className="flex" style={{ marginTop: '40px' }}>
                  {/* Doubled the number of SVGs per row from 8 to 16 */}
                  {Array.from({ length: 16 }).map((_, colIndex) => (
                    <div 
                      key={`icon1-${repeatIndex}-${colIndex}`} 
                      className="mx-20 my-20"
                    >
                      <img 
                        src={colIndex % 2 === 0 ? hatSvg : bottleSvg} 
                        alt={colIndex % 2 === 0 ? "Hat" : "Bottle"}
                        style={{
                          width: colIndex % 2 === 0 ? '150px' : '80px',
                          height: colIndex % 2 === 0 ? '140px' : '130px',
                          filter: 'brightness(0) invert(1)'
                        }}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Second row - offset by 35px */}
                <div className="flex" style={{ marginTop: '40px', marginLeft: '35px' }}>
                  {/* Doubled the number of SVGs per row from 8 to 16 */}
                  {Array.from({ length: 16 }).map((_, colIndex) => (
                    <div 
                      key={`icon2-${repeatIndex}-${colIndex}`} 
                      className="mx-20 my-20"
                    >
                      <img 
                        src={colIndex % 2 === 0 ? bottleSvg : hatSvg} 
                        alt={colIndex % 2 === 0 ? "Bottle" : "Hat"}
                        style={{
                          width: colIndex % 2 === 0 ? '80px' : '150px',
                          height: colIndex % 2 === 0 ? '130px' : '140px',
                          filter: 'brightness(0) invert(1)'
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <style>{`
          /* Scrolling animations as requested */
          @keyframes movePattern {
            0% {
              transform: translate(0, 0);
            }
            100% {
              transform: translate(548px, -680px);
            }
          }
          
          @keyframes movePatternTopLeft {
            0% {
              transform: translate(0, 0);
            }
            100% {
              transform: translate(548px, -680px);
            }
          }
          
          @keyframes slowPulse {
            0% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.05);
              opacity: 1;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }

          /* Button press animation */
          @keyframes buttonPress {
            0% {
              transform: scale(1);
            }
            50% {
              transform: scale(0.95);
            }
            100% {
              transform: scale(1);
            }
          }

          .animate-button-press {
            animation: buttonPress 150ms ease-out;
          }
        `}</style>
        
        {/* Top-left scene with border - Positioned to match Potion section in SVG */}
        <div 
          className="absolute"
          style={{ 
            clipPath: 'polygon(88% 0, 100% 12%, 100% 81%, 82% 100%, 0 100%, 0 48%, 0 0)', 
            backgroundColor: '#000',
            width: '490px',
            height: '444px',
            top: 'calc(50vh - 410px)',
            left: 'calc(50vw - 108px)'
          }}
        >
            {/* Absolutely positioned background image - UPDATED for polygon shape */}
            <div 
              className="absolute"
              style={{ 
                width: '490px',
                height: '460px',
                top: 0,
                left: 0,
                backgroundImage: 'url(/img/potion_room.png)',
                backgroundPosition: 'center center',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat'
              }}
            />
            <div 
              className="absolute"
              style={{ 
                width: '490px',
                height: '460px',
                top: 0,
                left: 0,
                transformOrigin: 'center center',
                overflow: 'hidden'
              }}
            >
                <DEAI3DScene 
                  className="w-full h-full"
                  style={{ 
                    width: '490px',
                    height: '460px',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }}
                  placeholderImgSrc="img/loading-placeholder.png"
                  vrmUrl={"avatar/Witch.vrm"}
                  vrmaUrl={"animations/Shiryu/Idle/Shiryu Idle 2024-10-23 Take_000_FBX_1.vrma"}
                  vrmaLibRoot={"animations/Shiryu/"}
                  vrmaLibConfig={"AnimationConfig.json"}
                  animatorLibRoot={"animators/Shiryu/"}
                  animatorLibConfig={"AnimatorConfig.json"}
                  cameraPosition={{ x: -0.0, y: 1.4, z: -1.5}}
                />
            </div>
        </div>
        
        {/* Bottom scene with border - Positioned to match Omikuji section in SVG */}
        <div 
          className="absolute"
          style={{ 
            clipPath: 'polygon(88% 0, 100% 12%, 100% 81%, 82% 100%, 0 100%, 0 48%, 0 0)',
            backgroundColor: '#000',
            width: '490px',
            height: '444px',
            top: 'calc(50vh + 135px)',
            left: 'calc(50vw - 108px)'
          }}
        > 
            {/* Absolutely positioned background image */}
            <div 
              className="absolute"
              style={{ 
                width: '490px',
                height: '444px',
                top: 0,
                left: 0,
                backgroundImage: 'url(/img/SHRINE_BG.png)',
                backgroundPosition: 'center center',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat'
              }}
            />
            <div 
              className="absolute"
              style={{ 
                width: '490px',
                height: '444px',
                top: 0,
                left: 0,
                transformOrigin: 'center center',
                overflow: 'hidden'
              }}
            >
          <DEAI3DScene 
            className="w-full h-full"
            style={{ 
              width: '490px',
              height: '444px',
              position: 'absolute',
              top: 0,
              left: 0
            }}
            placeholderImgSrc="img/loading-placeholder.png"
            vrmUrl={"avatar/Miko.vrm"}
            vrmaUrl={"animations/Noura/Idle/QATAR Idle_clean_02_upd.vrma"}
            vrmaLibRoot={"animations/Noura/"}
            vrmaLibConfig={"AnimationConfig.json"}
            animatorLibRoot={"animators/Noura/"}
            animatorLibConfig={"AnimatorConfig.json"}
            cameraPosition={{ x: 0.0, y: 1.4, z: -1.4 }}
          />
            </div>
        </div>
        
        {/* Potion Logo - positioned to match the Potion scene */}
        <div 
          className="absolute"
          style={{
            left: 'calc(50vw - 108px)',
            top: 'calc(50vh - 410px)',
            width: '210px',
            height: '210px',
            zIndex: 10,
            animation: 'slowPulse 8s ease-in-out infinite'
          }}
        >
          <img 
            src="/img/potionlogo.png"
            alt="Potion Logo"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </div>

        
        {/* Omikuji Logo - positioned to match the Omikuji scene */}
        <div 
          className="absolute"
          style={{
            left: 'calc(50vw - 108px)',
            top: 'calc(50vh + 135px)',
            width: '210px',
            height: '210px',
            zIndex: 10,
            animation: 'slowPulse 8s ease-in-out infinite'
          }}
        >
          <img 
            src="/img/omikujilogo.png"
            alt="Omikuji Logo"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </div>

      <div className="absolute top-5 right-5">
          <p className="text-right text-xs md:text-sm">v{import.meta.env.VITE_APP_VERSION}</p>
      </div>
    </ClickEnhancer>
  );
};